/**
 * What an alert is about and what its rule found, read from its `detail` (PRD-0075 R-B5): a
 * phrase and its samples, a new connection's other end, the keys a state change named, the
 * nodes a merge folded in, the watched entities it made one, the node a source came in under.
 * Pure; `detail` is JSON from the server, so every field is checked.
 */
import type { AlertFinding, AlertObjectRow } from '@/components/monitor/AlertObject'
import { RULE_SEVERITIES } from '@/components/monitor/SeverityBadge'
import type { AlertDto, RuleSeverity } from '@/services/api/case-monitor'

type Detail = Record<string, unknown>

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null

const records = (value: unknown): Detail[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is Detail => typeof entry === 'object' && entry !== null)
    : []

const texts = (values: readonly unknown[]): string[] =>
  values.map(text).filter((value): value is string => value !== null)

export function ruleSeverity(value: string): RuleSeverity | null {
  return RULE_SEVERITIES.find(severity => severity === value) ?? null
}

const nodeNames = (value: unknown): string[] =>
  texts(records(value).map(node => node.name ?? node.uid))

/** What a merge found (slice 08): watched entities made one, the nodes folded in, the node a
 * source came in under. */
function mergeFinding(detail: Detail): AlertFinding | null {
  const identity = nodeNames(detail.identity)
  if (identity.length > 1) return { kind: 'identity', names: identity }
  const merged = nodeNames(detail.merged)
  if (merged.length > 0) return { kind: 'merged', names: merged }
  const [via] = nodeNames([detail.via_merge])
  return via !== undefined ? { kind: 'viaMerge', name: via } : null
}

function finding(alert: AlertDto, detail: Detail): AlertFinding | null {
  const keys = texts(records(detail.changes).map(change => change.key))
  if (keys.length > 0) return { kind: 'changes', keys: [...new Set(keys)] }
  const merge = mergeFinding(detail)
  if (merge !== null) return merge
  const sample = text(alert.sample)
  return sample !== null && alert.rule_kind !== 'occurrence'
    ? { kind: 'sample', text: sample }
    : null
}

/** The alert's object and finding as the inbox row shows them. */
export function alertObject(alert: AlertDto): AlertObjectRow {
  const detail: Detail = alert.detail ?? {}
  const isPair = alert.rule_kind === 'new_connection'
  return {
    subjectLabel: alert.subject_label,
    phrase: alert.rule_kind === 'phrase' ? text(detail.phrase) : null,
    otherLabel: isPair ? (text(detail.object_name) ?? alert.object_uid ?? null) : null,
    videoEvent: alert.rule_kind === 'video_event' ? videoEventOf(detail) : null,
    finding: finding(alert, detail),
  }
}

/** A video event alert's event: its kind, when it began, its verdict. */
function videoEventOf(detail: Detail): AlertObjectRow['videoEvent'] {
  const event = (detail.video_event ?? {}) as Detail
  const kind = text(event.kind)
  if (!kind) return null
  return {
    kind,
    tStart: typeof event.t_start === 'number' ? event.t_start : 0,
    verdict: text(event.verdict) ?? 'unverified',
  }
}
